import { Shift, ShiftReactionStatus, ShiftStatus, TeacherShift } from 'types/collections';

import { ShiftTab } from '@components/Shifts/interfaces';

// Re-export ShiftReactionStatus for convenience
export { ShiftReactionStatus };

/**
 * Translation keys for teacher display messages
 */
export enum TeacherDisplayTranslationKeys {
  FUTURE = 'schoolShifts.withSubstituteFuture',
  CURRENT = 'schoolShifts.withSubstituteCurrent',
  PAST = 'schoolShifts.withSubstitutePast',
}

/**
 * Map ShiftTab to teacher context for consistent translation
 */
export const mapShiftTabToContext = (tab: ShiftTab): 'future' | 'current' | 'past' => {
  switch (tab) {
    case 'upcoming':
      return 'future';
    case 'ongoing':
      return 'current';
    case 'pastShifts':
      return 'past';
    default:
      return 'current';
  }
};

/**
 * Get translation key for teacher/substitute display based on context or automatic timing detection
 */
export const getTeacherDisplayTranslationKey = (
  context?: 'future' | 'current' | 'past' | ShiftTab,
  shiftDate?: string,
  timeFrom?: string,
  timeUntil?: string,
): string => {
  // Convert ShiftTab to context if needed
  if (
    context &&
    ['notifications', 'upcoming', 'ongoing', 'pastShifts'].includes(context as string)
  ) {
    context = mapShiftTabToContext(context as ShiftTab);
  }

  // Use provided context if available
  if (context) {
    switch (context) {
      case 'future':
        return TeacherDisplayTranslationKeys.FUTURE;
      case 'current':
        return TeacherDisplayTranslationKeys.CURRENT;
      case 'past':
        return TeacherDisplayTranslationKeys.PAST;
      default:
        return TeacherDisplayTranslationKeys.CURRENT;
    }
  }

  // Fallback to automatic detection if context not provided
  if (shiftDate && timeFrom && timeUntil) {
    return ShiftFilters.getTeacherDisplayTranslationKeyByTiming(shiftDate, timeFrom, timeUntil);
  }

  // Default fallback
  return TeacherDisplayTranslationKeys.CURRENT;
};

/**
 * Shared utilities for filtering shifts by status and time
 */

export const ShiftFilters = {
  /**
   * Get the correct teacher display translation key based on shift timing
   */
  getTeacherDisplayTranslationKeyByTiming: (
    shiftDate: string,
    timeFrom: string,
    timeUntil: string,
  ): TeacherDisplayTranslationKeys => {
    const isOngoing = ShiftFilters.isOngoingShift(shiftDate, timeFrom, timeUntil);
    const isFuture = ShiftFilters.isFutureShift(shiftDate, timeFrom);
    const isPast = ShiftFilters.isPastShift(shiftDate, timeUntil);

    if (isOngoing) {
      return TeacherDisplayTranslationKeys.CURRENT;
    } else if (isFuture) {
      return TeacherDisplayTranslationKeys.FUTURE;
    } else if (isPast) {
      return TeacherDisplayTranslationKeys.PAST;
    }

    // Default to future if we can't determine
    return TeacherDisplayTranslationKeys.FUTURE;
  },

  /**
   * Check if a shift has teachers with a specific status
   */
  hasReactionsWithStatus: (shift: Shift, status: ShiftReactionStatus): boolean => {
    return shift.shift_reactions?.some((reaction: any) => reaction.status === status) || false;
  },

  /**
   * Count reactions with a specific status
   */
  countReactionsWithStatus: (shift: Shift, status: ShiftReactionStatus): number => {
    return (
      shift.shift_reactions?.filter((reaction: any) => reaction.status === status)?.length || 0
    );
  },

  /**
   * Check if shift is in the future
   */
  isFutureShift: (shiftDate: string, timeFrom: string): boolean => {
    const shiftDateTime = new Date(`${shiftDate}T${timeFrom}`);
    return shiftDateTime > new Date();
  },

  /**
   * Check if shift is currently ongoing
   */
  isOngoingShift: (shiftDate: string, timeFrom: string, timeTill: string): boolean => {
    const now = new Date();
    const startTime = new Date(`${shiftDate}T${timeFrom}`);
    const endTime = new Date(`${shiftDate}T${timeTill}`);
    return now >= startTime && now <= endTime;
  },

  /**
   * Check if shift is in the past
   */
  isPastShift: (shiftDate: string, timeTill: string): boolean => {
    const shiftEndTime = new Date(`${shiftDate}T${timeTill}`);
    return shiftEndTime < new Date();
  },

  /**
   * Filter school shifts for notifications tab (shifts that need attention: applied teachers OR open shifts)
   */
  getNotificationShifts: (shifts: Shift[]): Shift[] => {
    return shifts.filter((shift) => {
      const hasAppliedTeachers = ShiftFilters.hasReactionsWithStatus(
        shift,
        ShiftReactionStatus.APPLIED,
      );
      const isShiftOpen = shift.status === ShiftStatus.OPEN; // Use proper shift status
      const isFutureShift = !ShiftFilters.isPastShift(shift.shift_date, shift.time_till);

      // Show shifts that either have applied teachers OR are still open for applications (and not in the past)
      // This ensures that rejected teachers don't make the shift disappear - it stays open for others
      return (hasAppliedTeachers || isShiftOpen) && isFutureShift;
    });
  },

  /**
   * Filter school shifts for upcoming tab (shifts with approved teachers)
   */
  getUpcomingShifts: (shifts: Shift[]): Shift[] => {
    return shifts.filter((shift) => {
      const isShiftFilled = shift.status === ShiftStatus.FILLED; // Use proper shift status
      const hasTeacherAssigned = shift.teacher_id; // Also check if teacher is assigned
      const isFutureShift = ShiftFilters.isFutureShift(shift.shift_date, shift.time_from);

      // Show upcoming shifts that either have FILLED status OR have a teacher assigned
      return (isShiftFilled || hasTeacherAssigned) && isFutureShift;
    });
  },

  /**
   * Filter school shifts for ongoing tab
   */
  getOngoingShifts: (shifts: Shift[]): Shift[] => {
    return shifts.filter((shift) => {
      const isShiftFilled = shift.status === ShiftStatus.FILLED; // Use proper shift status
      const hasTeacherAssigned = shift.teacher_id; // Also check if teacher is assigned
      const isOngoingShift = ShiftFilters.isOngoingShift(
        shift.shift_date,
        shift.time_from,
        shift.time_till,
      );

      // Show ongoing shifts that either have FILLED status OR have a teacher assigned
      return (isShiftFilled || hasTeacherAssigned) && isOngoingShift;
    });
  },

  /**
   * Filter school shifts for past tab (completed shifts with assigned teachers)
   */
  getPastShifts: (shifts: Shift[]): Shift[] => {
    return shifts.filter((shift) => {
      const isShiftFilled = shift.status === ShiftStatus.FILLED;
      const hasTeacherAssigned = !!shift.teacher_id;
      const isPastShift = ShiftFilters.isPastShift(shift.shift_date, shift.time_till);

      // Show past shifts that either have FILLED status OR have a teacher assigned
      return (isShiftFilled || hasTeacherAssigned) && isPastShift;
    });
  },

  /**
   * Filter teacher shifts for notifications tab
   */
  getTeacherNotificationShifts: (shifts: TeacherShift[]): TeacherShift[] => {
    return shifts.filter((shift) => {
      const statusIncludes = [
        ShiftReactionStatus.NOT_REACTED,
        ShiftReactionStatus.REJECTED,
        ShiftReactionStatus.APPLIED,
      ].includes(shift?.reaction_status || ShiftReactionStatus.NOT_REACTED);
      return statusIncludes && !ShiftFilters.isPastShift(shift.shift_date, shift.time_till);
    });
  },

  /**
   * Filter teacher shifts for upcoming tab
   */
  getTeacherUpcomingShifts: (shifts: TeacherShift[]): TeacherShift[] => {
    return shifts.filter((shift) => {
      return (
        shift.reaction_status === ShiftReactionStatus.APPROVED &&
        ShiftFilters.isFutureShift(shift.shift_date, shift.time_from)
      );
    });
  },

  /**
   * Filter teacher shifts for ongoing tab
   */
  getTeacherOngoingShifts: (shifts: TeacherShift[]): TeacherShift[] => {
    return shifts.filter((shift) => {
      return (
        shift.reaction_status === ShiftReactionStatus.APPROVED &&
        ShiftFilters.isOngoingShift(shift.shift_date, shift.time_from, shift.time_till)
      );
    });
  },

  /**
   * Filter teacher shifts for past tab
   */
  getTeacherPastShifts: (shifts: TeacherShift[]): TeacherShift[] => {
    return shifts.filter((shift) => {
      return (
        shift.reaction_status === ShiftReactionStatus.APPROVED &&
        ShiftFilters.isPastShift(shift.shift_date, shift.time_till)
      );
    });
  },
};

/**
 * Status utilities
 */
export const ShiftStatusUtils = {
  /**
   * Get display text for reaction status
   */
  getStatusText: (status: ShiftReactionStatus): string => {
    switch (status) {
      case ShiftReactionStatus.NOT_REACTED:
        return 'Not Reacted';
      case ShiftReactionStatus.APPLIED:
        return 'Applied';
      case ShiftReactionStatus.APPROVED:
        return 'Approved';
      case ShiftReactionStatus.REJECTED:
        return 'Rejected';
      case ShiftReactionStatus.REFUSED:
        return 'Refused';
      default:
        return 'Unknown';
    }
  },

  /**
   * Check if status allows school action (approve/reject)
   */
  canSchoolTakeAction: (status: ShiftReactionStatus): boolean => {
    return status === ShiftReactionStatus.APPLIED;
  },
};
